// =============================================
// useAllZones — zony ze wszystkich kont CF z cache w stanie.
//
// LENIWE: nic nie pobiera na mount (zakladka Konta nie ma strzelac do CF).
// refresh() wola ZonesTab ("Pobierz domeny") i kreator (krok 1 — tam zony sa
// obowiazkowe, inaczej plan proponowalby zone_create dla domen z zona).
// appendZone() dopisuje swiezo utworzona zone bez pelnego odswiezenia.
//
// Hook trzymany w CloudflarePage (nie w zakladce), bo Radix odmontowuje
// nieaktywne TabsContent — cache ma przezyc przelaczenie zakladek.
// =============================================
import { useCallback, useMemo, useRef, useState } from "react";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import { listZonesAllAccounts, type CfZone, type CfZoneWithAccount, type ListZonesAllResult } from "@/services/cloudflareService";
import { normalizeDomain } from "@/services/aftermarketService";

export interface AllZonesState {
  zones: CfZoneWithAccount[];
  failures: ListZonesAllResult["failures"];
  zoneCountByAccount: Record<string, number>;
  loading: boolean;
  /** null = jeszcze nie pobrano */
  loadedAt: number | null;
  refresh: () => Promise<void>;
  appendZone: (zone: CfZone, cfAccountId: string) => void;
  /** Zona po znormalizowanej nazwie domeny (bez www/protokolu). */
  findZone: (domainKey: string) => CfZoneWithAccount | null;
}

export function useAllZones(accounts: CloudflareAccount[]): AllZonesState {
  const [zones, setZones] = useState<CfZoneWithAccount[]>([]);
  const [failures, setFailures] = useState<ListZonesAllResult["failures"]>([]);
  const [zoneCountByAccount, setZoneCountByAccount] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const inFlight = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async () => {
    // Dwa wywolania naraz (ZonesTab + kreator) → jedno pobranie.
    if (inFlight.current) return inFlight.current;
    const p = (async () => {
      setLoading(true);
      try {
        const res = await listZonesAllAccounts(accountsRef.current);
        setZones(res.zones);
        setFailures(res.failures);
        setZoneCountByAccount(res.zoneCountByAccount);
        setLoadedAt(Date.now());
      } finally {
        setLoading(false);
        inFlight.current = null;
      }
    })();
    inFlight.current = p;
    return p;
  }, []);

  const appendZone = useCallback((zone: CfZone, cfAccountId: string) => {
    const acc = accountsRef.current.find((a) => a.id === cfAccountId);
    setZones((prev) => {
      if (prev.some((z) => z.id === zone.id)) return prev;
      return [
        ...prev,
        { ...zone, cfAccountId, cfAccountLabel: acc?.label ?? "?", cfAccountHasAccountId: !!acc?.account_id?.trim() },
      ];
    });
    setZoneCountByAccount((prev) => ({ ...prev, [cfAccountId]: (prev[cfAccountId] ?? 0) + 1 }));
  }, []);

  const byName = useMemo(() => {
    const m = new Map<string, CfZoneWithAccount>();
    for (const z of zones) {
      const k = normalizeDomain(z.name);
      if (k && !m.has(k)) m.set(k, z);
    }
    return m;
  }, [zones]);

  const findZone = useCallback((domainKey: string) => byName.get(domainKey) ?? null, [byName]);

  return { zones, failures, zoneCountByAccount, loading, loadedAt, refresh, appendZone, findZone };
}
